import moment from "moment";
import api from "./Api";
import { handleApiError } from "../utils/apiUtils";
import { preventDuplicateRequest } from "../utils/requestManager";

const UserManagement = process.env.REACT_APP_USER_MGMT_URL || "";

if (!UserManagement) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_USER_MGMT_URL in your environment variables."
  );
}
const Picklist = process.env.REACT_APP_PICKLIST_URL || "";

if (!Picklist) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_PICKLIST_URL in your environment variables."
  );
}

const Case = process.env.REACT_APP_CASE_URL || "";

if (!Case) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables."
  );
}

const picklistAPI = api(Picklist);
const userManagementAPI = api(UserManagement);
const caseAPI = api(Case);

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

interface TeamMember {
  memberuserid: number;
  memberfullname: string;
  memberphonenumber: string;
  memberfaxnumber: string;
  memberemailid: string;
  memberroledesc: string;
}

interface CaseTeamHODDetailsResult {
  tenantId: number;
  caseInfoId: number;
  caseId: string;
  teamId: number;
  teamName: string;
  description: string;
  teamPhoneNumber: string;
  teamFaxNumber: string;
  teamEmailAddress: string;
  hodUserId: number;
  hodFullName: string;
  hodPhoneNumber: string;
  hodFaxNumber: string;
  hodEmailId: string;
  hodRoleDesc: string;
  teamMembersJson: string;
}

export interface CaseStageHistory {
  caseStageHistoryId: number;
  caseInfoId: number;
  previousStage: string;
  currentStage: string;
  addedByName: string;
  addedOn: string | null;
  elapsedTime: string | null;
  elapsedTimeDescription: string;
}

export interface GetCaseStageHistoryResponse {
  isSuccess: boolean;
  statusCode: number;
  message: string;
  result: CaseStageHistory[];
  timestamp: string;
}

//Fetches the list of Picklist by group name for the dropdowns.
const fetchPickListByGroupName = async (params: any): Promise<any> => {
  try {
    const response = await picklistAPI.post(
      "/api/picklist/items/bygroupname",
      params
    );
    // Map the result to transform pickListItemId into value and displayName into label
    const list = response.data?.result.map((item: any) => ({
      value: item.pickListItemId,
      label: item.displayName,
    }));
    return list;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

//Fetches the Case header information
const fetchCaseheaderinfo = async (ID: any): Promise<any> => {
  const requestKey = `fetchCaseheaderinfo_${ID}`;
  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await caseAPI.get(
        `/api/case/information/details?caseinfoid=${ID}`,
      );

      const data = {
        ...response.data.result,
        dateOfRegistration: moment(
          response.data.result.dateOfRegistration,
        ).format("MM/DD/YYYY"),
      };

      return data;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw new Error("Unable to fetch the data. Please try again later.");
    }
  });
};

//Fetches the Register Case Button Status
const fetchRegisterCaseButtonStatus = async (ID: any): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/case/information/process/status?caseinfoid=${ID}`
    );

    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error fetching data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

//Add case header details.
const addCaseheaderDetails = async (
  userData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.post<ApiResponse<any>>(
      "/api/case/information/add",
      userData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the case header details. Please check the data and try again."
    );
  }
};

//Update case header details.
const updateCaseheaderDetails = async (
  userData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.put<ApiResponse<any>>(
      "/api/case/information/update",
      userData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the case header details.. Please check the data and try again."
    );
  }
};

export const searchCaseList = async (params: any): Promise<any> => {
  try {
    const response = await caseAPI.post("/api/case/search", params);
    const updatedResult = response.data?.result.map((searchlist: any) => ({
      ...searchlist,
      actions: { Edit: "Edit" },
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

export const advanceSearchCaseList = async (params: any): Promise<any> => {
  try {
    const response = await caseAPI.post("/api/case/search/advance", params);
    const updatedResult = response.data?.result.map((searchlist: any) => ({
      ...searchlist,
      actions: { Edit: "Edit" },
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

//Fetches the list of stakeholder client etc. for the dropdowns.
const fetchCaseStakeholderList = async (
  params: any,
  isActive: any,
): Promise<any> => {
  const requestKey = `fetchCaseStakeholderList_${params}_${isActive}`;
  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await caseAPI.get(
        `/api/case/stakeholder/list?stakeholdertypecode=${params}&isActive=${isActive}`,
      );
      const list = response.data?.result.map((item: any) => ({
        value: item.stakeholderId,
        label: item.stakeholderName,
      }));
      return list;
    } catch (error) {
      console.error("Error fetching the data:", error);
      throw new Error("Unable to fetch the data. Please try again later.");
    }
  });
};

//Fetches the list of stakeholder address for the dropdowns.
const fetchCaseStakeholderAddressList = async (params: any): Promise<any> => {
  if (!params) return [];
  try {
    const response = await caseAPI.get(
      `/api/case/stakeholder/addresses?stakeholderid=${params}`
    );
    const list = response.data?.result.map((item: any) => ({
      value: item.stakeholderaddressid,
      label: item.detailedinformation?.split(" (P)")[0],
    }));
    return list;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

//Fetches the list of Stakeholder Contacts for the dropdowns.
const fetchCaseStakeholderContactList = async (params: any): Promise<any> => {
  if (!params) return [];
  try {
    const response = await caseAPI.get(
      `/api/case/stakeholder/contacts?stakeholderid=${params}`
    );
    const list = response.data?.result.map((item: any) => ({
      value: item.stakeholdercontactid,
      label: item.detailedinformation?.split(" (E)")[0],
    }));
    return list;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

const addCaseStakeholderDetails = async (
  userData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.post<ApiResponse<any>>(
      "/api/case/stakeholder/add",
      userData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new data:", error);
    throw new Error(
      "Failed to add details. Please check the data and try again."
    );
  }
};

const updateCaseStakeholderDetails = async (
  userData: any
): Promise<ApiResponse<any>> => {
  try {
    const response = await caseAPI.put<ApiResponse<any>>(
      "/api/case/stakeholder/update",
      userData
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new data:", error);
    throw new Error(
      "Failed to add details. Please check the data and try again."
    );
  }
};

const fetchCaseStakeholderDetails = async (
  ID: any,
  Code: any,
): Promise<any> => {
  const requestKey = `fetchCaseStakeholderDetails_${ID}_${Code}`;
  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await caseAPI.get(
        `/api/case/stakeholder/details?caseinfoid=${ID}&stakeholdertype=${Code}`,
      );
      return response.data?.result;
    } catch (error) {
      console.error("Error fetching the data:", error);
      throw new Error("Unable to fetch the data. Please try again later.");
    }
  });
};

const fetchCaseTeamHODDetails = async (
  caseInfoId: number,
): Promise<CaseTeamHODDetailsResult> => {
  const requestKey = `fetchCaseTeamHODDetails_${caseInfoId}`;
  return preventDuplicateRequest(requestKey, async () => {
    try {
      const response = await caseAPI.get(
        `/api/case/stakeholder/team/details?caseinfoid=${caseInfoId}`,
      );
      return response.data?.result;
    } catch (error) {
      console.error("Error fetching case team HOD details:", error);
      throw new Error(
        "Unable to fetch case team HOD details. Please try again later.",
      );
    }
  });
};

const fetchPartiesDetails = async (caseInfoId: number): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/case/information/parties?caseinfoid=${caseInfoId}`
    );
    const result = response.data?.result;
    if (!result) return [];
    const list = Array.isArray(result)
      ? result.map((item: any) => ({
          value: String(item.partyId),
          label: item.partyDisplayName,
          partyCode: item.partyCode,
        }))
      : [
          {
            value: String(result.partyId),
            label: result.partyDisplayName,
            partyCode: result.partyCode,
          },
        ];

    return list;
  } catch (error) {
    console.error("Error fetching parties details:", error);
    throw new Error(
      "Unable to fetch case parties details. Please try again later."
    );
  }
};

const fetchStageStatusLogDetails = async (
  caseInfoId: number
): Promise<CaseStageHistory[]> => {
  try {
    const response = await caseAPI.get(
      `/api/case/activity/stagehistory?caseinfoid=${caseInfoId}`
    );
    return response.data?.result ?? [];
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const fetchValideQMEDoctorScheduling = async (qmeDoctorUserId: number) => {
  try {
    const response = await caseAPI.get(
      `/api/case/stakeholder/validate/qmedoctor-scheduling?qmeDoctorUserId=${qmeDoctorUserId}`
    );
    return response.data?.result ?? [];
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const fetchSenderListForTODO = async (caseInfoId: number): Promise<any> => {
  try {
    const response = await caseAPI.get(
      `/api/case/information/access/user/list?caseinfoid=${caseInfoId}`
    );
    const result = response.data?.result;
    if (!result) return [];
    const list = Array.isArray(result)
      ? result.map((item: any) => ({
          value: String(item.userId),
          label: item.userDetails,
          partyCode: "US",
        }))
      : [
          {
            value: String(result.userId),
            label: result.userDetails,
            partyCode: "US",
          },
        ];

    return list;
  } catch (error) {
    console.error("Error fetching parties details:", error);
    throw new Error(
      "Unable to fetch case parties details. Please try again later."
    );
  }
};

export {
  fetchPickListByGroupName,
  addCaseheaderDetails,
  updateCaseheaderDetails,
  fetchCaseheaderinfo,
  fetchCaseStakeholderList,
  fetchCaseStakeholderAddressList,
  fetchCaseStakeholderContactList,
  addCaseStakeholderDetails,
  updateCaseStakeholderDetails,
  fetchCaseStakeholderDetails,
  fetchRegisterCaseButtonStatus,
  fetchCaseTeamHODDetails,
  fetchPartiesDetails,
  fetchStageStatusLogDetails,
  fetchValideQMEDoctorScheduling,
  fetchSenderListForTODO,
};
